import { cardPeriodForDate } from "./finance";
import { cardPaymentEventId } from "./card-payments";
import type { CardPaymentTimingAdjustment, FinancialSnapshot } from "./types";

export interface CardPaymentTimingInput {
  bankCreditId: string;
  paymentPeriod: string;
  pairedChargeCents: number;
  reason: string;
}

function periodStart(period: string): string | null {
  return /^(\d{4}-\d{2}-\d{2}) al \d{4}-\d{2}-\d{2}$/.exec(period)?.[1] ?? null;
}

function previousDate(date: string): string {
  const prior = new Date(`${date}T12:00:00Z`);
  prior.setUTCDate(prior.getUTCDate() - 1);
  return prior.toISOString().slice(0, 10);
}

/** Positive on the posting cut, negative on the earlier payment cut. */
export function paymentTimingForCut(adjustments: CardPaymentTimingAdjustment[], card: string, period: string): number {
  return adjustments.reduce((sum, item) => {
    if (item.card !== card) return sum;
    if (!Number.isSafeInteger(item.amountCents) || item.amountCents <= 0) throw new Error("El ajuste entre cortes no tiene centavos válidos.");
    const next = sum + (item.sourcePeriod === period ? item.amountCents : 0) - (item.paymentPeriod === period ? item.amountCents : 0);
    if (!Number.isSafeInteger(next)) throw new Error("Los ajustes entre cortes exceden el importe permitido.");
    return next;
  }, 0);
}

export function planCardPaymentTiming(
  snapshot: FinancialSnapshot,
  input: CardPaymentTimingInput,
  operationId: string,
  cutDay: number,
  paidIds: string[],
): CardPaymentTimingAdjustment {
  const credit = snapshot.bankCredits?.find((item) => item.id === input.bankCreditId);
  if (!credit) throw new Error("No encontré la bonificación bancaria.");
  if (!Number.isInteger(cutDay) || cutDay < 1 || cutDay > 31) throw new Error("El día de corte no es válido.");
  const sourceStart = periodStart(credit.period);
  if (!sourceStart || cardPeriodForDate(previousDate(sourceStart), cutDay) !== input.paymentPeriod) {
    throw new Error("El pago debe pertenecer al corte inmediatamente anterior a la bonificación.");
  }
  if (!Number.isSafeInteger(input.pairedChargeCents) || input.pairedChargeCents < 0 || input.pairedChargeCents > 100) {
    throw new Error("El cargo y su reversión deben ser centavos enteros entre $0.00 y $1.00.");
  }
  if (!input.reason.trim() || input.reason.trim().length > 500) throw new Error("Explica la aplicación entre cortes.");
  if (paidIds.includes(cardPaymentEventId(credit.card, credit.period)) || paidIds.includes(cardPaymentEventId(credit.card, input.paymentPeriod))) {
    throw new Error("Uno de los cortes ya fue pagado en Banculem.");
  }
  const existing = snapshot.cardPaymentTimingAdjustments ?? [];
  if (existing.some((item) => item.bankCreditId === credit.id || item.id === operationId)) {
    throw new Error("La bonificación ya tiene una aplicación entre cortes.");
  }
  if (!Number.isSafeInteger(credit.amountCents) || credit.amountCents <= 0) throw new Error("La bonificación no tiene un importe válido.");
  const amountCents = credit.amountCents + input.pairedChargeCents;
  if (!Number.isSafeInteger(amountCents) || amountCents <= 0) throw new Error("El ajuste no tiene un importe válido.");
  return {
    id: operationId,
    operationId,
    card: credit.card,
    bankCreditId: credit.id,
    sourcePeriod: credit.period,
    paymentPeriod: input.paymentPeriod,
    amountCents,
    pairedChargeCents: input.pairedChargeCents,
    reason: input.reason.trim(),
    createdAt: new Date().toISOString(),
  };
}
